/**
 * EquationStudio — drop-in equation editor for the teacher Content Studio.
 *
 * Capabilities:
 *   • Live KaTeX preview with `mhchem` (chemistry) enabled
 *   • Symbol palette (math, Greek, calculus, sets, arrows, physics, chemistry)
 *   • AI Assistant (Claude Sonnet 4.5 via Emergent LLM Key):
 *       - Generate from natural language     → /equation/generate
 *       - Extract from pasted text/transcript → /equation/extract
 *       - Step-by-step Solve                  → /equation/solve
 *       - Explain term-by-term                → /equation/explain
 *       - Chemistry Balance                   → /equation/balance
 *   • "Insert into section" callback hands back rich block (LaTeX + plain + steps)
 *
 * Works as a panel (drawer) or inline. Identical contract for web & mobile —
 * the entire intelligence lives in the shared backend.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import 'katex/dist/contrib/mhchem.js'; // chemistry extension — adds \ce{} support
import {
  X, Calculator, Sigma, Sparkles, Loader2, Wand2, AlertTriangle, Check,
  Atom, BookOpen, ChevronDown, ChevronUp, Copy, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#34A853';
const SOFT = '#F0FDF4';
const DARK = '#0C1A12';
const INK = '#1F2937';

function authHeaders() {
  const t = localStorage.getItem('desky_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const TABS = [
  { id: 'compose',  label: 'Compose',  icon: Wand2,       sub: 'Type or paste an equation' },
  { id: 'generate', label: 'Generate', icon: Sparkles,    sub: 'Natural language → LaTeX' },
  { id: 'extract',  label: 'Extract',  icon: BookOpen,    sub: 'Find equations in text' },
  { id: 'balance',  label: 'Balance',  icon: Atom,        sub: 'Chemistry coefficients' },
];

const PALETTE_FALLBACK = {
  basic: [['+','+'],['−','-'],['×','\\times'],['÷','\\div'],['=','='],['≠','\\ne'],['≈','\\approx'],['≤','\\le'],['≥','\\ge'],['±','\\pm']],
  powers_fractions: [['x²','^{2}'],['x³','^{3}'],['√','\\sqrt{}'],['a/b','\\frac{}{}']],
  calculus: [['∑','\\sum'],['∫','\\int'],['d/dx','\\frac{d}{dx}'],['∂','\\partial'],['lim','\\lim_{ \\to }']],
  greek: [['α','\\alpha'],['β','\\beta'],['γ','\\gamma'],['Δ','\\Delta'],['θ','\\theta'],['λ','\\lambda'],['μ','\\mu'],['π','\\pi'],['σ','\\sigma'],['ω','\\omega']],
  physics: [['Vector','\\vec{}'],['Newton','\\,\\text{N}'],['Joule','\\,\\text{J}'],['m/s²','\\,\\text{m/s}^{2}'],['kg','\\,\\text{kg}']],
  chemistry: [['→ rxn','\\ce{->}'],['⇌','\\ce{<=>}'],['(g)','(g)'],['(l)','(l)'],['(aq)','(aq)'],['H2O','\\ce{H2O}'],['CO2','\\ce{CO2}'],['H+','\\ce{H+}']],
  sets_logic: [['∈','\\in'],['∉','\\notin'],['∪','\\cup'],['∩','\\cap'],['∞','\\infty']],
  arrows: [['→','\\to'],['⇔','\\Leftrightarrow'],['⇒','\\Rightarrow']],
};

function safeLatex(latex) {
  // Strip outer $..$ if present
  if (!latex) return '';
  let s = latex.trim();
  if (s.startsWith('$$') && s.endsWith('$$')) s = s.slice(2, -2);
  if (s.startsWith('$') && s.endsWith('$')) s = s.slice(1, -1);
  return s;
}

function KaTeX({ latex, displayMode = true }) {
  const s = safeLatex(latex);
  if (!s) return null;
  try {
    return displayMode ? <BlockMath math={s}/> : <InlineMath math={s}/>;
  } catch {
    return <code className="text-xs text-red-600">[LaTeX error] {s}</code>;
  }
}

export default function EquationStudio({ open, onClose, defaultLatex = '', subjectHint = null, onInsert, contextText = '' }) {
  const [tab, setTab] = useState('compose');
  const [palette, setPalette] = useState(PALETTE_FALLBACK);
  const [latex, setLatex] = useState(defaultLatex);
  const [busy, setBusy] = useState(false);
  const [displayMode, setDisplayMode] = useState(true);
  const [subject, setSubject] = useState(subjectHint || 'auto');
  const taRef = useRef(null);

  // Generate
  const [genText, setGenText] = useState('');
  const [genResult, setGenResult] = useState(null);

  // Extract
  const [extractText, setExtractText] = useState(contextText || '');
  const [extracted, setExtracted] = useState([]);

  // Solve / Explain
  const [solveResult, setSolveResult] = useState(null);
  const [explainResult, setExplainResult] = useState(null);

  // Balance
  const [balText, setBalText] = useState('');
  const [balResult, setBalResult] = useState(null);

  // Load palette
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/teacher/studio/equation/palette`, { headers: authHeaders() });
        if (r.data?.palette) setPalette(r.data.palette);
      } catch { /* keep fallback */ }
    })();
  }, [open]);

  // Reset when toggled
  useEffect(() => {
    if (!open) {
      setSolveResult(null); setExplainResult(null);
    } else if (defaultLatex) {
      setLatex(defaultLatex);
    }
  }, [open, defaultLatex]);

  const insertToken = (token) => {
    const ta = taRef.current;
    if (!ta) return setLatex(prev => prev + token);
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = latex.slice(0, start) + token + latex.slice(end);
    setLatex(next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + token.length;
      ta.setSelectionRange(cursor, cursor);
    });
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const doGenerate = async () => {
    if (!genText.trim()) return toast.error('Type a description');
    setBusy(true); setGenResult(null);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/generate`,
        { description: genText, subject_hint: subject === 'auto' ? null : subject },
        { headers: authHeaders() });
      setGenResult(r.data);
      setLatex(r.data.latex);
      setTab('compose');
      toast.success(`Generated: ${r.data.title || 'equation'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generate failed');
    } finally { setBusy(false); }
  };

  const doExtract = async () => {
    if (!extractText.trim()) return toast.error('Paste some text first');
    setBusy(true); setExtracted([]);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/extract`,
        { text: extractText, subject_hint: subject === 'auto' ? null : subject },
        { headers: authHeaders() });
      setExtracted(r.data.equations || []);
      toast.success(`Found ${r.data.equations?.length || 0} equation(s)`);
    } catch (e) {
      toast.error('Extract failed');
    } finally { setBusy(false); }
  };

  const doSolve = async () => {
    if (!latex.trim()) return toast.error('Enter an equation first');
    setBusy(true); setSolveResult(null);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/solve`,
        { latex, subject_hint: subject === 'auto' ? null : subject },
        { headers: authHeaders() });
      setSolveResult(r.data);
    } catch (e) {
      toast.error('Solve failed');
    } finally { setBusy(false); }
  };

  const doExplain = async () => {
    if (!latex.trim()) return toast.error('Enter an equation first');
    setBusy(true); setExplainResult(null);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/explain`,
        { latex, subject_hint: subject === 'auto' ? null : subject },
        { headers: authHeaders() });
      setExplainResult(r.data);
    } catch (e) {
      toast.error('Explain failed');
    } finally { setBusy(false); }
  };

  const doBalance = async () => {
    if (!balText.trim()) return toast.error('Enter a reaction');
    setBusy(true); setBalResult(null);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/balance`,
        { equation: balText }, { headers: authHeaders() });
      setBalResult(r.data);
      setLatex(r.data.balanced_latex);
      setSubject('chemistry');
      setTab('compose');
      toast.success('Balanced');
    } catch (e) {
      toast.error('Balance failed');
    } finally { setBusy(false); }
  };

  const insertIntoSection = () => {
    if (!onInsert) return;
    onInsert({
      latex,
      subject,
      title: genResult?.title || balResult?.input || 'Equation',
      plain: genResult?.explanation || explainResult?.concept || null,
      steps: solveResult?.steps || [],
      variables: genResult?.variables || explainResult?.terms || [],
      mode: displayMode ? 'display' : 'inline',
    });
    toast.success('Inserted into section');
    onClose?.();
  };

  const copyLatex = async () => {
    try { await navigator.clipboard.writeText(latex); toast.success('LaTeX copied'); }
    catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[65] flex" style={{ background: 'rgba(8,16,12,0.72)' }} data-testid="equation-studio">
      <div className="ml-auto h-full w-full sm:w-[720px] lg:w-[840px] bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1.5">
              <Calculator className="w-3 h-3"/> Equation Studio · Claude Sonnet 4.5 · KaTeX + mhchem
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Math, Physics &amp; Chemistry — done structured
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Compose with the symbol palette, generate from natural language, balance reactions, solve step-by-step.
            </p>
          </div>
          <button onClick={onClose} data-testid="eq-close" className="text-slate-400 hover:text-slate-700 p-1 -m-1 rounded-md hover:bg-slate-100">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-4 border-b border-slate-100">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`eq-tab-${t.id}`}
              className={`px-3 py-2.5 text-left transition-colors border-b-2 ${
                tab === t.id ? 'border-emerald-500 bg-emerald-50/40' : 'border-transparent hover:bg-slate-50'
              }`}>
              <div className="flex items-start gap-2">
                <t.icon className="w-3.5 h-3.5 mt-0.5" style={{ color: tab === t.id ? GREEN : '#64748B' }}/>
                <div className="min-w-0">
                  <div className="text-[12px] font-bold text-slate-800 truncate">{t.label}</div>
                  <div className="text-[10px] text-slate-500 truncate -mt-0.5">{t.sub}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Subject pill */}
        <div className="px-5 pt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Subject</span>
          {['auto', 'math', 'physics', 'chemistry', 'biology'].map(s => (
            <button key={s} onClick={() => setSubject(s)}
              data-testid={`eq-subj-${s}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                subject === s ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}>{s}</button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === 'compose' && (
            <ComposePane
              latex={latex} setLatex={setLatex}
              displayMode={displayMode} setDisplayMode={setDisplayMode}
              taRef={taRef} insertToken={insertToken} palette={palette}
              onSolve={doSolve} onExplain={doExplain} solveResult={solveResult} explainResult={explainResult}
              busy={busy} onCopy={copyLatex}
            />
          )}
          {tab === 'generate' && (
            <GeneratePane text={genText} setText={setGenText} result={genResult} busy={busy} onRun={doGenerate}/>
          )}
          {tab === 'extract' && (
            <ExtractPane text={extractText} setText={setExtractText} items={extracted} busy={busy} onRun={doExtract}
              onPick={(e) => { setLatex(e.latex); setSubject(e.subject || 'auto'); setTab('compose'); }}/>
          )}
          {tab === 'balance' && (
            <BalancePane text={balText} setText={setBalText} result={balResult} busy={busy} onRun={doBalance}
              onUse={() => { setLatex(balResult?.balanced_latex || ''); setSubject('chemistry'); setTab('compose'); }}/>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between gap-2">
          <div className="text-[11px] text-slate-500 max-w-md truncate">
            {latex ? <>LaTeX: <code className="bg-slate-100 px-1 py-0.5 rounded text-[10px]">{latex.slice(0, 80)}{latex.length > 80 ? '…' : ''}</code></> : '—'}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyLatex} data-testid="eq-copy" disabled={!latex}
              className="h-9 px-3 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Copy className="w-3.5 h-3.5"/> Copy LaTeX
            </button>
            {onInsert && (
              <button onClick={insertIntoSection} disabled={!latex}
                data-testid="eq-insert"
                className="h-9 px-4 text-xs font-bold text-white rounded-lg inline-flex items-center gap-1.5 shadow hover:shadow-md disabled:opacity-50"
                style={{ background: GREEN }}>
                <Plus className="w-3.5 h-3.5"/> Insert into section
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Compose pane ────────────────────────────────────────────────────────────
function ComposePane({ latex, setLatex, displayMode, setDisplayMode, taRef, insertToken, palette, onSolve, onExplain, solveResult, explainResult, busy, onCopy }) {
  const [expanded, setExpanded] = useState({ basic: true, powers_fractions: true, greek: true, chemistry: false, calculus: false, physics: false, sets_logic: false, arrows: false });
  return (
    <div className="space-y-4">
      {/* Preview card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Preview</span>
          <div className="flex items-center gap-1.5 text-[10px]">
            <button onClick={() => setDisplayMode(true)}
                    className={`px-2 py-0.5 rounded font-semibold ${displayMode ? 'bg-emerald-100 text-emerald-700' : 'text-slate-500 hover:bg-slate-100'}`}>
              Block
            </button>
            <button onClick={() => setDisplayMode(false)}
                    className={`px-2 py-0.5 rounded font-semibold ${!displayMode ? 'bg-emerald-100 text-emerald-700' : 'text-slate-500 hover:bg-slate-100'}`}>
              Inline
            </button>
          </div>
        </div>
        <div className="min-h-[60px] flex items-center justify-center bg-slate-50 rounded-lg p-4" data-testid="eq-preview">
          {latex ? <KaTeX latex={latex} displayMode={displayMode}/> : <span className="text-xs text-slate-400">Live KaTeX preview appears here</span>}
        </div>
      </div>

      {/* Editor */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">LaTeX source</label>
        <textarea
          ref={taRef}
          value={latex}
          onChange={(e) => setLatex(e.target.value)}
          rows={3}
          data-testid="eq-latex-input"
          placeholder="Type LaTeX or tap a symbol below — e.g. F = m \\, a"
          className="mt-2 w-full font-mono text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
        />
        <div className="mt-2 flex gap-2 flex-wrap">
          <button onClick={onSolve} disabled={busy || !latex} data-testid="eq-solve-btn"
            className="h-9 px-3 text-xs font-semibold text-white rounded-lg inline-flex items-center gap-1.5 shadow disabled:opacity-50"
            style={{ background: GREEN }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Calculator className="w-3.5 h-3.5"/>}
            Solve step-by-step
          </button>
          <button onClick={onExplain} disabled={busy || !latex} data-testid="eq-explain-btn"
            className="h-9 px-3 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-white hover:bg-emerald-50 rounded-lg inline-flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5"/> Explain
          </button>
        </div>
      </div>

      {/* Symbol palette */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest inline-flex items-center gap-1.5"><Sigma className="w-3 h-3"/> Symbol palette</span>
        </div>
        <div className="p-3 space-y-2 max-h-[260px] overflow-y-auto" data-testid="eq-palette">
          {Object.entries(palette).map(([group, items]) => (
            <div key={group} className="rounded-md bg-slate-50/50">
              <button onClick={() => setExpanded(prev => ({ ...prev, [group]: !prev[group] }))}
                className="w-full px-2.5 py-1.5 flex items-center justify-between text-left">
                <span className="text-[11px] font-bold text-slate-700 capitalize tracking-wide">{group.replace('_', ' ')}</span>
                {expanded[group] ? <ChevronUp className="w-3 h-3 text-slate-400"/> : <ChevronDown className="w-3 h-3 text-slate-400"/>}
              </button>
              {expanded[group] && (
                <div className="px-2 pb-2 flex flex-wrap gap-1.5">
                  {items.map(([label, token], i) => (
                    <button key={`${group}-${i}`} onClick={() => insertToken(token)}
                      data-testid={`eq-sym-${group}-${i}`}
                      title={token}
                      className="min-w-[44px] h-8 px-2 rounded-md bg-white border border-slate-200 text-xs font-mono hover:bg-emerald-50 hover:border-emerald-300 transition-colors">
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Solve result */}
      {solveResult && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="eq-solve-result">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-2">
            <Check className="w-3 h-3"/> Step-by-step solution · solving for {solveResult.solving_for}
          </div>
          <ol className="space-y-2.5">
            {(solveResult.steps || []).map((s, i) => (
              <li key={i} className="bg-white rounded-lg border border-emerald-100 p-2.5">
                <div className="text-[11px] font-bold text-slate-600 mb-1">Step {s.order}. {s.description}</div>
                <div className="text-[11px] text-slate-500 mb-1 italic">{s.rule}</div>
                <KaTeX latex={s.latex}/>
              </li>
            ))}
          </ol>
          {solveResult.final && (
            <div className="mt-3 bg-white rounded-lg border-2 border-emerald-300 p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Final</div>
              <KaTeX latex={solveResult.final.latex}/>
              {solveResult.final.unit && <div className="text-[11px] text-slate-500 mt-1">Unit: {solveResult.final.unit}</div>}
            </div>
          )}
          {solveResult.common_mistakes?.length > 0 && (
            <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <strong className="block mb-1">⚠ Common mistakes:</strong>
              <ul className="list-disc pl-4 space-y-0.5">
                {solveResult.common_mistakes.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Explain result */}
      {explainResult && (
        <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-4" data-testid="eq-explain-result">
          <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700 mb-2">Explanation · {explainResult.name}</div>
          <p className="text-sm text-slate-700 mb-3">{explainResult.concept}</p>
          {(explainResult.terms || []).length > 0 && (
            <table className="w-full text-[11px] mt-2">
              <thead><tr className="text-slate-500"><th className="text-left px-2 py-1">Symbol</th><th className="text-left px-2 py-1">Name</th><th className="text-left px-2 py-1">Meaning</th><th className="text-left px-2 py-1">Unit</th></tr></thead>
              <tbody>
                {explainResult.terms.map((t, i) => (
                  <tr key={i} className="border-t border-sky-100"><td className="px-2 py-1 font-mono">{t.sym}</td><td className="px-2 py-1 font-semibold">{t.name}</td><td className="px-2 py-1">{t.meaning}</td><td className="px-2 py-1">{t.unit}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {explainResult.example && (
            <div className="mt-3 bg-white rounded-lg p-3 border border-sky-100">
              <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700 mb-1">Example</div>
              <p className="text-xs text-slate-700 mb-2">{explainResult.example.prompt}</p>
              <KaTeX latex={explainResult.example.latex}/>
              {explainResult.example.answer && <p className="text-xs font-semibold text-emerald-700 mt-1">→ {explainResult.example.answer}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Generate pane ───────────────────────────────────────────────────────────
function GeneratePane({ text, setText, result, busy, onRun }) {
  return (
    <div className="space-y-3">
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Describe in plain English</label>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
        data-testid="eq-gen-text"
        placeholder="e.g. the quadratic formula · the ideal gas law · derivative of sin x · F = ma with units"
        className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-emerald-200 focus:outline-none"/>
      <button onClick={onRun} disabled={busy || !text.trim()} data-testid="eq-gen-btn"
        className="h-10 px-5 text-sm font-bold text-white rounded-lg shadow inline-flex items-center gap-2 disabled:opacity-50"
        style={{ background: GREEN }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
        Generate equation
      </button>
      {result && (
        <div className="rounded-xl border border-emerald-200 bg-white p-4 space-y-2" data-testid="eq-gen-result">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">{result.subject} · {result.kind}</div>
          <div className="text-sm font-bold text-slate-800">{result.title}</div>
          <div className="bg-slate-50 rounded-lg p-3"><KaTeX latex={result.latex}/></div>
          {(result.alt_forms || []).length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-slate-500 uppercase mt-2 mb-1">Alternative forms</div>
              {result.alt_forms.map((f, i) => <div key={i} className="bg-slate-50 rounded-lg p-2 mt-1"><KaTeX latex={f}/></div>)}
            </div>
          )}
          {(result.variables || []).length > 0 && (
            <div className="text-[11px]">
              <div className="font-bold text-slate-600 mt-2 mb-1">Variables</div>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                {result.variables.map((v, i) => <li key={i} className="text-slate-600"><code className="font-bold text-slate-800">{v.sym}</code> — {v.name} <span className="text-slate-400">({v.unit})</span></li>)}
              </ul>
            </div>
          )}
          {result.explanation && <p className="text-xs text-slate-600 italic">{result.explanation}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Extract pane ────────────────────────────────────────────────────────────
function ExtractPane({ text, setText, items, busy, onRun, onPick }) {
  return (
    <div className="space-y-3">
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Paste transcript / OCR / notes</label>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={5}
        data-testid="eq-extract-text"
        placeholder="Paste a lecture transcript, board OCR, or any text containing math/chemistry…"
        className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-emerald-200 focus:outline-none"/>
      <button onClick={onRun} disabled={busy || !text.trim()} data-testid="eq-extract-btn"
        className="h-10 px-5 text-sm font-bold text-white rounded-lg shadow inline-flex items-center gap-2 disabled:opacity-50"
        style={{ background: GREEN }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <BookOpen className="w-4 h-4"/>}
        Extract equations
      </button>
      {items.length > 0 && (
        <ul className="space-y-2" data-testid="eq-extract-list">
          {items.map((e, i) => (
            <li key={e.id || i} className="rounded-xl border border-slate-200 bg-white p-3 hover:border-emerald-300 transition-colors cursor-pointer"
                onClick={() => onPick?.(e)}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">{e.subject || 'math'} · {e.kind || 'formula'}</span>
                {e.title && <span className="text-[11px] font-bold text-slate-700">{e.title}</span>}
              </div>
              <div className="bg-slate-50 rounded p-2"><KaTeX latex={e.latex}/></div>
              {e.context_snippet && <p className="text-[10px] text-slate-400 mt-1 italic truncate">"{e.context_snippet}"</p>}
              <div className="text-[10px] text-emerald-700 mt-2 font-semibold">→ Tap to load into editor</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Balance pane ────────────────────────────────────────────────────────────
function BalancePane({ text, setText, result, busy, onRun, onUse }) {
  return (
    <div className="space-y-3">
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Unbalanced chemical equation</label>
      <input value={text} onChange={e => setText(e.target.value)}
        data-testid="eq-balance-input"
        placeholder="e.g. C3H8 + O2 -> CO2 + H2O"
        className="w-full text-sm font-mono rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-emerald-200 focus:outline-none"/>
      <button onClick={onRun} disabled={busy || !text.trim()} data-testid="eq-balance-btn"
        className="h-10 px-5 text-sm font-bold text-white rounded-lg shadow inline-flex items-center gap-2 disabled:opacity-50"
        style={{ background: GREEN }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Atom className="w-4 h-4"/>}
        Balance reaction
      </button>
      {result && (
        <div className="rounded-xl border border-emerald-200 bg-white p-4" data-testid="eq-balance-result">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">{result.type || 'reaction'}</div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mt-2"><KaTeX latex={result.balanced_latex}/></div>
          <div className="grid grid-cols-2 gap-3 mt-3 text-[11px]">
            <div>
              <div className="font-bold text-slate-600 mb-1">Reactants</div>
              {(result.reactants || []).map((r, i) => (
                <div key={i} className="text-slate-700"><code className="font-bold">{r.formula}</code> <span className="text-slate-400">({r.state})</span> · {r.name}</div>
              ))}
            </div>
            <div>
              <div className="font-bold text-slate-600 mb-1">Products</div>
              {(result.products || []).map((r, i) => (
                <div key={i} className="text-slate-700"><code className="font-bold">{r.formula}</code> <span className="text-slate-400">({r.state})</span> · {r.name}</div>
              ))}
            </div>
          </div>
          {result.atom_check && (
            <div className="mt-3 text-[11px] bg-slate-50 rounded p-2">
              <div className="font-bold text-slate-600 mb-1">Atom check</div>
              {Object.entries(result.atom_check).map(([atom, line]) => (
                <div key={atom} className="text-slate-600 font-mono"><strong>{atom}:</strong> {line}</div>
              ))}
            </div>
          )}
          <button onClick={onUse} className="mt-3 h-9 px-3 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-white hover:bg-emerald-50 rounded-lg inline-flex items-center gap-1.5">
            Use this in Compose →
          </button>
        </div>
      )}
    </div>
  );
}
